import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Clock, MessageSquare, ChevronDown, ChevronUp, CheckCircle, EyeOff } from 'lucide-react';
import { api, fetchImportPdf, getReportNotes } from '../../utils/api';
import { detectFileViewerType, getFileNameFromPath } from '../../utils/fileViewer';
import { Card } from '../Card/Card';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import API_ENDPOINTS from '../../config/apiConfig';
import AlertNotesModal from './AlertNotesModal';
import FileViewerModal from '../FileViewerModal';

// ---------------------------------------------------------------------------
// Category badge config
// ---------------------------------------------------------------------------
export const CATEGORY_CONFIG = {
  comp_rooms:      { label: 'COMP',  bg: 'var(--lodge-orange-50)',    color: 'var(--lodge-orange-600)' },
  ar_risk:         { label: 'A/R',   bg: 'var(--color-info-bg)',      color: 'var(--color-info-ink)' },
  rate_integrity:  { label: 'RATE',  bg: 'var(--color-info-bg)',      color: '#0369A1' },
  operational:     { label: 'OPS',   bg: 'var(--color-error-bg)',     color: '#B42318' },
  labor:           { label: 'LABOR', bg: 'var(--color-warning-bg)',   color: '#92400E' },
  data_quality:    { label: 'DATA',  bg: 'var(--lodge-onyx-100)',     color: 'var(--lodge-onyx-600)' },
  trend:           { label: 'TREND', bg: 'var(--color-success-bg)',   color: '#027A48' },
};

export const SEVERITY_DOT = {
  high:   'var(--color-error,   #F04438)',
  medium: 'var(--color-warning, #F79009)',
  low:    'var(--lodge-onyx-300, #A0A0AE)',
};

// ---------------------------------------------------------------------------
// NightAuditAlerts — main card
// ---------------------------------------------------------------------------
export function NightAuditAlerts() {
  const { currentOrganization, userSettings } = useUserSettingsContext();
  const [flags, setFlags] = useState([]);
  const [highCount, setHighCount] = useState(0);
  const [mediumCount, setMediumCount] = useState(0);
  const [analyzedAt, setAnalyzedAt] = useState(null);
  const [filter, setFilter] = useState('all'); // all | high | medium | open
  const [loading, setLoading] = useState(true);
  const [resolvingAll, setResolvingAll] = useState(false);

  const orgId = currentOrganization?.id;
  const [noteModalFlag, setNoteModalFlag] = useState(null); // full flag object for modal
  const [fileViewer, setFileViewer] = useState(null); // { url, fileType, initialPage }
  const [noteCounts, setNoteCounts] = useState(new Map()); // flagId → note count
  const [preloadedNotes, setPreloadedNotes] = useState(new Map()); // flagId → notes array
  const [visibleCount, setVisibleCount] = useState(5);
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 5;

  // Load actual notes from CosmosDB for all flags in a single call, then group by pageId (flag.id)
  const fetchAllNotes = useCallback(async (flagList) => {
    if (!orgId || !flagList.length) return;
    try {
      const res = await getReportNotes({ orgId, reportId: 'night-audit-alerts' });
      const allNotes = (res.notes || []).filter(n => !n.isDeleted);
      const counts = new Map();
      const cache = new Map();
      for (const note of allNotes) {
        const flagId = Number(note.pageId);
        if (!cache.has(flagId)) cache.set(flagId, []);
        cache.get(flagId).push(note);
      }
      for (const [flagId, notes] of cache) {
        counts.set(flagId, notes.length);
      }
      setNoteCounts(counts);
      setPreloadedNotes(cache);
    } catch { /* non-fatal */ }
  }, [orgId]);

  const openFile = useCallback(async (blobPath, sourcePage) => {
    const fileType = detectFileViewerType(blobPath, 'text');
    const url = await fetchImportPdf(blobPath);
    setFileViewer({ url, fileType, fileName: getFileNameFromPath(blobPath), initialPage: sourcePage || null });
  }, []);

  const orgCode = currentOrganization?.code;
  const locationIds = userSettings?.orgAccess?.[orgCode]?.locationIds || [];

  const fetchAlerts = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const locParam = locationIds.length ? `&locationIds=${locationIds.join(',')}` : '';
      const res = await api.get(`${API_ENDPOINTS.NIGHT_AUDIT_ALERTS}?orgId=${orgId}${locParam}`);
      const data = res.data;
      const fetchedFlags = data.flags || [];
      setFlags(fetchedFlags);
      setHighCount(data.highCount || 0);
      setMediumCount(data.mediumCount || 0);
      // Use the latest analyzedAt across all flags
      const dates = fetchedFlags.map(f => f.analyzedAt).filter(Boolean);
      if (dates.length) setAnalyzedAt(new Date(Math.max(...dates.map(d => new Date(d)))));
      fetchAllNotes(fetchedFlags);
    } catch (err) {
      console.error('Failed to fetch night audit alerts', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, locationIds, fetchAllNotes]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const removeFlag = (id) => {
    const flag = flags.find(f => f.id === id);
    setFlags(prev => prev.filter(f => f.id !== id));
    if (flag?.severity === 'high') setHighCount(prev => Math.max(0, prev - 1));
    if (flag?.severity === 'medium') setMediumCount(prev => Math.max(0, prev - 1));
  };

  const resolve = async (id, resolutionNote) => {
    removeFlag(id);
    try {
      await api.patch(API_ENDPOINTS.NIGHT_AUDIT_ALERTS, { action: 'resolve', id, resolutionNote: resolutionNote || undefined });
    } catch (err) {
      console.error('Failed to resolve alert', err);
      fetchAlerts();
    }
  };

  const ignore = async (id) => {
    removeFlag(id);
    try {
      await api.patch(API_ENDPOINTS.NIGHT_AUDIT_ALERTS, { action: 'ignore', id });
    } catch (err) {
      console.error('Failed to ignore alert', err);
      fetchAlerts();
    }
  };

  const resolveAll = async () => {
    if (!orgId) return;
    setResolvingAll(true);
    try {
      await api.patch(API_ENDPOINTS.NIGHT_AUDIT_ALERTS, { action: 'resolveAll', orgId });
      setFlags([]);
      setHighCount(0);
      setMediumCount(0);
    } catch (err) {
      console.error('Failed to resolve all', err);
    } finally {
      setResolvingAll(false);
    }
  };

  const filteredFlags = flags.filter(f => {
    if (filter === 'high') return f.severity === 'high';
    if (filter === 'medium') return f.severity === 'medium';
    return true;
  });

  // Reset pagination when filter changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filter]);

  const visibleFlags = filteredFlags.slice(0, visibleCount);
  const hasMore = visibleCount < filteredFlags.length;

  // Infinite scroll — load more when sentinel enters viewport
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisibleCount(c => c + PAGE_SIZE); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, visibleFlags.length]);

  const formattedTime = analyzedAt
    ? `${analyzedAt.toLocaleDateString([], { month: 'numeric', day: 'numeric' })} ${analyzedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : null;

  const hotelCount = new Set(flags.map(f => f.locationId || f.hotelName)).size;

  if (!loading && flags.length === 0) return null;

  return (
    <Card
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Night Audit Alerts
          {highCount > 0 && (
            <Badge color="#EF4444" bg="#FEF2F2">{highCount} High</Badge>
          )}
          {mediumCount > 0 && (
            <Badge color="#F97316" bg="#FFF7ED">{mediumCount} Medium</Badge>
          )}
        </span>
      }
      right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FilterBar filter={filter} onChange={setFilter} />
          <ResolveAllButton onClick={resolveAll} loading={resolvingAll} />
        </div>
      }
    >
      {loading ? (
        <SkeletonList />
      ) : (
        <>
          {/* Scrollable alert list — fixed height with infinite scroll */}
          <div style={{
            maxHeight: 340,
            overflowY: 'auto',
            overflowX: 'hidden',
            marginRight: -4,
            paddingRight: 4,
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {visibleFlags.map((flag, idx) => (
                <AlertRow
                  key={flag.id}
                  flag={flag}
                  isLast={idx === visibleFlags.length - 1 && !hasMore}
                  onResolve={(note) => resolve(flag.id, note)}
                  onIgnore={() => ignore(flag.id)}
                  onOpenNotes={() => setNoteModalFlag(flag)}
                  onOpenFile={openFile}
                  noteCount={noteCounts.get(flag.id) || 0}
                />
              ))}
              {filteredFlags.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--lodge-onyx-400)', textAlign: 'center', padding: '20px 0' }}>
                  No alerts match this filter.
                </p>
              )}
              {/* Sentinel — triggers loading next page when scrolled into view */}
              {hasMore && (
                <div ref={sentinelRef} style={{ padding: '10px 0', textAlign: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)' }}>
                    Loading…
                  </span>
                </div>
              )}
            </div>
          </div>
          {flags.length > 0 && (
            <footer style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid var(--lodge-onyx-100)',
            }}>
              <span style={{ fontSize: 12, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)' }}>
                {visibleFlags.length} of {filteredFlags.length} alert{filteredFlags.length !== 1 ? 's' : ''}
                {formattedTime && ` · Analyzed ${formattedTime}`}
                {hotelCount > 0 && ` · ${hotelCount} hotel${hotelCount !== 1 ? 's' : ''}`}
              </span>
              <a
                href="/dashboard/night-audit"
                style={{ fontSize: 12, color: 'var(--lodge-orange-400, #FF7A00)', fontWeight: 600, fontFamily: 'var(--font-sans)', textDecoration: 'none' }}
              >
                View alerts →
              </a>
            </footer>
          )}
        </>
      )}
      {noteModalFlag && (
        <AlertNotesModal
          flag={noteModalFlag}
          orgId={orgId}
          initialNotes={preloadedNotes.get(noteModalFlag.id)}
          onClose={() => {
            setNoteModalFlag(null);
            fetchAllNotes(flags);
          }}
          onNoteCountChange={() => fetchAllNotes(flags)}
        />
      )}
      {fileViewer && (
        <FileViewerModal
          url={fileViewer.url}
          fileType={fileViewer.fileType}
          fileName={fileViewer.fileName}
          initialPage={fileViewer.initialPage}
          onClose={() => setFileViewer(null)}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AlertRow
// ---------------------------------------------------------------------------
export function AlertRow({ flag, isLast, onResolve, onIgnore, onOpenNotes, onOpenFile, noteCount = 0, resolvedInfo }) {
  const hasNotes = noteCount > 0;
  const [expanded, setExpanded] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [resolving, setResolving] = useState(false); // show inline resolve note input

  const handleResolveClick = () => setResolving(true);
  const handleResolveConfirm = (note) => { setResolving(false); onResolve(note); };
  const handleResolveCancel = () => setResolving(false);

  const catConfig = CATEGORY_CONFIG[flag.category] || { label: flag.category?.toUpperCase(), bg: '#F5F5F5', color: '#424242' };
  const dotColor = SEVERITY_DOT[flag.severity] || SEVERITY_DOT.low;

  const sourceFileName = flag.fileName || flag.blobPath?.split('/').pop() || 'Source file';

  const handleFileClick = async () => {
    if (!flag.blobPath || fileLoading) return;
    setFileLoading(true);
    try {
      await onOpenFile(flag.blobPath, flag.sourcePage);
    } catch (err) {
      console.error('Failed to open source file', err);
    } finally {
      setFileLoading(false);
    }
  };

  return (
    <div style={{
      padding: '14px 0',
      borderBottom: isLast ? 'none' : '1px solid var(--lodge-onyx-100)',
    }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* Severity dot */}
        <span style={{
          width: 8, height: 8,
          borderRadius: '50%',
          background: dotColor,
          flexShrink: 0,
          marginTop: 6,
        }} />

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span
              onClick={() => setExpanded(e => !e)}
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--lodge-onyx-800)',
                cursor: 'pointer',
                lineHeight: 1.35,
              }}
            >
              {flag.title}
            </span>
            {flag.recurrenceCount > 1 && (
              <RecurrenceBadge count={flag.recurrenceCount} />
            )}
            {flag.wasEscalated && (
              <EscalatedBadge />
            )}
          </div>

          {/* Subtitle */}
          <p style={{
            margin: '3px 0 0',
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            color: 'var(--lodge-onyx-500)',
            lineHeight: 1.4,
          }}>
            {flag.hotelName}
            {flag.description && ` · ${flag.description.split('.')[0]}.`}
          </p>

          {/* Expanded description + data points */}
          {expanded && (
            <div style={{
              marginTop: 10,
              padding: '10px 12px',
              background: 'var(--lodge-onyx-50)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--lodge-onyx-100)',
            }}>
              {flag.description && (
                <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--lodge-onyx-700)', lineHeight: 1.5 }}>
                  {flag.description}
                </p>
              )}
              {flag.dataPoints?.length > 0 && (
                <ul style={{ margin: '0 0 8px', paddingLeft: 16, fontSize: 12, color: 'var(--lodge-onyx-600)', lineHeight: 1.6 }}>
                  {flag.dataPoints.map((pt, i) => <li key={i}>{pt}</li>)}
                </ul>
              )}
              {flag.recommendedAction && (
                <p style={{ margin: 0, fontSize: 12, color: 'var(--lodge-onyx-500)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  <strong style={{ fontStyle: 'normal', color: 'var(--lodge-onyx-700)' }}>Action: </strong>
                  {flag.recommendedAction}
                </p>
              )}
            </div>
          )}

          {/* Prior resolution callout — shown when flag re-appeared after being marked resolved */}
          {flag.priorResolution && (
            <div style={{
              marginTop: 8,
              padding: '7px 10px',
              background: 'var(--color-warning-bg)',
              border: '1px solid #FDE68A',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              color: 'var(--lodge-onyx-700)',
              fontFamily: 'var(--font-sans)',
            }}>
              <strong style={{ color: 'var(--lodge-orange-500)' }}>⚠ Previously resolved</strong>
              {flag.priorResolution.resolvedAt && (
                <span style={{ color: 'var(--lodge-onyx-500)' }}>
                  {' '}on {new Date(flag.priorResolution.resolvedAt).toLocaleDateString()}
                </span>
              )}
              {flag.priorResolution.resolvedBy && (
                <span style={{ color: 'var(--lodge-onyx-500)' }}> by {flag.priorResolution.resolvedBy}</span>
              )}
              {flag.priorResolution.resolutionNote && (
                <span> — {flag.priorResolution.resolutionNote}</span>
              )}
            </div>
          )}

          {/* Inline resolve note input */}
          {resolving && (
            <ResolveNoteInput onConfirm={handleResolveConfirm} onCancel={handleResolveCancel} />
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {/* Source file */}
            {flag.blobPath && (
              <button
                onClick={handleFileClick}
                disabled={fileLoading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px',
                  fontSize: 12,
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--lodge-onyx-600)',
                  background: 'var(--lodge-onyx-50)',
                  border: '1px solid var(--lodge-onyx-200)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: fileLoading ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  opacity: fileLoading ? 0.6 : 1,
                }}
              >
                <FileText size={11} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {fileLoading ? 'Opening…' : sourceFileName}
                </span>
              </button>
            )}

            {/* Notes */}
            <button
              onClick={onOpenNotes}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px',
                fontSize: 12,
                fontFamily: 'var(--font-sans)',
                color: hasNotes ? 'var(--lodge-orange-600, #D97706)' : 'var(--lodge-onyx-600)',
                background: hasNotes ? 'var(--lodge-orange-50, #FFF7ED)' : 'transparent',
                border: `1px solid ${hasNotes ? 'var(--lodge-orange-200, #FED7AA)' : 'var(--lodge-onyx-200)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: hasNotes ? 600 : 400,
              }}
            >
              <MessageSquare size={11} fill={hasNotes ? 'var(--lodge-orange-600, #D97706)' : 'none'} />
              Notes{hasNotes ? ` (${noteCount})` : ''}
            </button>

            {/* Expand/collapse */}
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '3px 8px',
                fontSize: 12,
                fontFamily: 'var(--font-sans)',
                color: 'var(--lodge-onyx-500)',
                background: 'transparent',
                border: '1px solid var(--lodge-onyx-200)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
              }}
            >
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              {expanded ? 'Less' : 'Details'}
            </button>

            {/* Resolve */}
            {!resolving && !resolvedInfo && (
              <button
                onClick={handleResolveClick}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px',
                  fontSize: 12,
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 500,
                  color: '#027A48',
                  background: 'var(--color-success-bg)',
                  border: '1px solid #A9EFC5',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                }}
              >
                <CheckCircle size={11} />
                Resolve
              </button>
            )}

            {/* Ignore */}
            {!resolving && !resolvedInfo && (
              <button
                onClick={onIgnore}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px',
                  fontSize: 12,
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--lodge-onyx-400)',
                  background: 'transparent',
                  border: '1px solid var(--lodge-onyx-200)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                }}
              >
                <EyeOff size={11} />
                Ignore
              </button>
            )}

            {/* Resolved badge */}
            {resolvedInfo && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', fontSize: 12, fontFamily: 'var(--font-sans)',
                fontWeight: 500, color: '#027A48',
                background: 'var(--color-success-bg)',
                border: '1px solid #A9EFC5',
                borderRadius: 'var(--radius-sm)',
              }}>
                <CheckCircle size={11} />
                Resolved
              </span>
            )}
          </div>
        </div>

        {/* Right: score + category badge + time */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {flag.severityScore != null && (
              <span
                title={`Severity score: ${flag.severityScore}/100`}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: 'var(--font-mono, monospace)',
                  color: dotColor,
                  minWidth: 24,
                  textAlign: 'right',
                }}
              >
                {flag.severityScore}
              </span>
            )}
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'var(--font-sans)',
              letterSpacing: '0.5px',
              padding: '2px 6px',
              borderRadius: 'var(--radius-sm)',
              background: catConfig.bg,
              color: catConfig.color,
            }}>
              {catConfig.label}
            </span>
          </div>
          {flag.analyzedAt && (
            <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>
              {new Date(flag.analyzedAt).toLocaleDateString([], { month: 'numeric', day: 'numeric' })}{' '}
              {new Date(flag.analyzedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* Resolved info banner */}
      {resolvedInfo && (
        <div style={{
          marginTop: 8,
          padding: '8px 12px',
          background: 'var(--color-success-bg, #ECFDF3)',
          border: '1px solid #A9EFC5',
          borderRadius: 'var(--radius-md)',
          fontSize: 12,
          fontFamily: 'var(--font-sans)',
          color: '#027A48',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
            <CheckCircle size={13} />
            Resolved by {resolvedInfo.resolvedBy || 'Unknown'}
            {resolvedInfo.resolvedAt && (
              <span style={{ fontWeight: 400, color: 'var(--lodge-onyx-500)' }}>
                · {new Date(resolvedInfo.resolvedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}{' '}
                {new Date(resolvedInfo.resolvedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
          {resolvedInfo.resolutionNote && (
            <div style={{ marginTop: 4, color: 'var(--lodge-onyx-600)', fontStyle: 'italic' }}>
              "{resolvedInfo.resolutionNote}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
export function Badge({ color, bg, children }) {
  return (
    <span style={{
      fontSize: 12,
      fontWeight: 600,
      fontFamily: 'var(--font-sans)',
      padding: '2px 8px',
      borderRadius: 'var(--radius-full)',
      background: bg,
      color,
    }}>
      {children}
    </span>
  );
}

function EscalatedBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 11,
      fontFamily: 'var(--font-sans)',
      fontWeight: 600,
      color: '#B42318',
      background: 'var(--color-error-bg)',
      border: '1px solid #FCA5A5',
      borderRadius: 'var(--radius-full)',
      padding: '1px 7px',
      whiteSpace: 'nowrap',
    }}>
      ↑ Escalated
    </span>
  );
}

function RecurrenceBadge({ count }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 11,
      fontFamily: 'var(--font-sans)',
      color: 'var(--lodge-onyx-400)',
      background: 'var(--lodge-onyx-100)',
      borderRadius: 'var(--radius-full)',
      padding: '1px 7px',
      whiteSpace: 'nowrap',
    }}>
      <Clock size={10} />
      {count} nights
    </span>
  );
}

export function FilterBar({ filter, onChange }) {
  const options = [
    { key: 'all', label: 'All' },
    { key: 'high', label: 'High' },
    { key: 'medium', label: 'Medium' },
  ];
  return (
    <div style={{ display: 'flex', gap: 2, background: 'var(--lodge-onyx-100)', borderRadius: 'var(--radius-md)', padding: 2 }}>
      {options.map(opt => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          style={{
            padding: '4px 10px',
            fontSize: 12,
            fontFamily: 'var(--font-sans)',
            fontWeight: filter === opt.key ? 600 : 400,
            color: filter === opt.key ? 'var(--lodge-onyx-800)' : 'var(--lodge-onyx-500)',
            background: filter === opt.key ? 'var(--lodge-white)' : 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            boxShadow: filter === opt.key ? 'var(--shadow-xs)' : 'none',
            transition: 'all var(--duration-fast)',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function ResolveAllButton({ onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        padding: '5px 12px',
        fontSize: 12,
        fontFamily: 'var(--font-sans)',
        fontWeight: 500,
        color: '#027A48',
        background: 'var(--color-success-bg)',
        border: '1px solid #A9EFC5',
        borderRadius: 'var(--radius-md)',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.6 : 1,
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
      }}
    >
      <CheckCircle size={13} />
      {loading ? 'Resolving…' : 'Resolve all'}
    </button>
  );
}

function ResolveNoteInput({ onConfirm, onCancel }) {
  const [note, setNote] = useState('');
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <input
        autoFocus
        type="text"
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="How was this resolved? (optional)"
        onKeyDown={e => {
          if (e.key === 'Enter') onConfirm(note);
          if (e.key === 'Escape') onCancel();
        }}
        style={{
          fontSize: 12,
          fontFamily: 'var(--font-sans)',
          border: '1px solid #A9EFC5',
          borderRadius: 'var(--radius-sm)',
          padding: '5px 8px',
          outline: 'none',
          color: 'var(--lodge-onyx-800)',
          background: 'var(--color-success-bg)',
        }}
      />
      <div style={{ display: 'flex', gap: 5 }}>
        <button
          onClick={() => onConfirm(note)}
          style={{
            padding: '3px 10px', fontSize: 12, fontFamily: 'var(--font-sans)',
            fontWeight: 600, color: '#fff', background: 'var(--color-success, #12B76A)',
            border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          }}
        >
          Confirm resolve
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '3px 8px', fontSize: 12, fontFamily: 'var(--font-sans)',
            color: 'var(--lodge-onyx-500)', background: 'transparent',
            border: '1px solid var(--lodge-onyx-200)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function SkeletonList() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} aria-busy="true" aria-label="Loading alerts…">
      {[1, 2, 3].map(i => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div className="lodge-skeleton" style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div className="lodge-skeleton" style={{ height: 14, width: '60%', marginBottom: 6 }} />
            <div className="lodge-skeleton" style={{ height: 12, width: '80%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}
